import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Maximize2, Download } from 'lucide-react';

/**
 * iter129 — App-wide Photo Lightbox / Image Viewer.
 *
 * Click any thumbnail anywhere in the app and the parent passes the image list
 * into <Lightbox open images startIndex onClose />. Supports:
 *   • Zoom in/out via buttons, mouse wheel, and pinch on touch devices
 *   • Pan when zoomed (drag with mouse or touch)
 *   • Prev / Next navigation with arrow keys, on-screen buttons, and swipe
 *   • Photo counter (Photo X of Y) + optional filename
 *   • Escape / click backdrop / close button to dismiss
 *   • Works on mobile portrait/landscape (uses object-fit + touch handlers)
 *
 * `images` is an array of either string URLs or {url, filename, alt} objects.
 */
const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.25;

const normalize = (img) => {
  if (!img) return null;
  if (typeof img === 'string') return { url: img, filename: '', alt: '' };
  return { url: img.url || img.src || '', filename: img.filename || '', alt: img.alt || '' };
};

const Lightbox = ({ open, images = [], startIndex = 0, onClose }) => {
  const list = (images || []).map(normalize).filter((i) => i && i.url);
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const pinchRef = useRef({ active: false, startDist: 0, startZoom: 1 });
  const containerRef = useRef(null);

  // Reset state whenever the lightbox opens or the index changes.
  useEffect(() => { if (open) setIdx(startIndex || 0); }, [open, startIndex]);
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [idx, open]);

  const current = list[idx];
  const total = list.length;

  const close = useCallback(() => { if (typeof onClose === 'function') onClose(); }, [onClose]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const zoomIn  = useCallback(() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2))), []);
  const zoomOut = useCallback(() => setZoom((z) => {
    const next_ = Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2));
    if (next_ === MIN_ZOOM) setPan({ x: 0, y: 0 });
    return next_;
  }), []);
  const reset = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);

  // Keyboard shortcuts.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape')      close();
      else if (e.key === 'ArrowLeft'  && total > 1) prev();
      else if (e.key === 'ArrowRight' && total > 1) next();
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-')                  zoomOut();
      else if (e.key === '0')                  reset();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, prev, next, total, zoomIn, zoomOut, reset]);

  // Body scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  // Mouse wheel zoom.
  const onWheel = (e) => {
    if (!open) return;
    e.preventDefault();
    if (e.deltaY < 0) zoomIn(); else zoomOut();
  };

  // Pan/drag handlers (mouse).
  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, originX: pan.x, originY: pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.active) return;
    setPan({ x: dragRef.current.originX + (e.clientX - dragRef.current.startX), y: dragRef.current.originY + (e.clientY - dragRef.current.startY) });
  };
  const endMouseDrag = () => { dragRef.current.active = false; };

  // Touch handlers — single-finger pan when zoomed, two-finger pinch for zoom + swipe to nav when not zoomed.
  const swipeRef = useRef({ startX: 0, startY: 0, moved: false });
  const distance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = { active: true, startDist: distance(e.touches[0], e.touches[1]), startZoom: zoom };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      if (zoom > 1) {
        dragRef.current = { active: true, startX: t.clientX, startY: t.clientY, originX: pan.x, originY: pan.y };
      } else {
        swipeRef.current = { startX: t.clientX, startY: t.clientY, moved: false };
      }
    }
  };
  const onTouchMove = (e) => {
    if (pinchRef.current.active && e.touches.length === 2) {
      const dist = distance(e.touches[0], e.touches[1]);
      const ratio = dist / (pinchRef.current.startDist || 1);
      const next_ = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +(pinchRef.current.startZoom * ratio).toFixed(2)));
      setZoom(next_);
      if (next_ === MIN_ZOOM) setPan({ x: 0, y: 0 });
      return;
    }
    if (dragRef.current.active && e.touches.length === 1) {
      const t = e.touches[0];
      setPan({ x: dragRef.current.originX + (t.clientX - dragRef.current.startX), y: dragRef.current.originY + (t.clientY - dragRef.current.startY) });
      return;
    }
    if (e.touches.length === 1 && zoom <= 1) {
      const t = e.touches[0];
      if (Math.abs(t.clientX - swipeRef.current.startX) > 8) swipeRef.current.moved = true;
    }
  };
  const onTouchEnd = (e) => {
    if (pinchRef.current.active && e.touches.length < 2) pinchRef.current.active = false;
    if (dragRef.current.active && e.touches.length === 0) dragRef.current.active = false;
    if (zoom <= 1 && swipeRef.current.moved && e.changedTouches.length === 1 && total > 1) {
      const dx = e.changedTouches[0].clientX - swipeRef.current.startX;
      if (dx > 60) prev();
      else if (dx < -60) next();
    }
    swipeRef.current.moved = false;
  };

  // Double-click to toggle zoom.
  const onDoubleClick = () => { if (zoom > 1) reset(); else setZoom(2); };

  if (!open || !current) return null;

  const view = (
    <div
      data-testid="lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-black/95 flex flex-col select-none"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      onWheel={onWheel}
      onMouseUp={endMouseDrag}
      onMouseLeave={endMouseDrag}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-white">
        <div className="text-xs text-white/70 truncate">
          {total > 1 && <span data-testid="lightbox-counter">Photo {idx + 1} of {total}</span>}
          {current.filename && <span className="ml-2 text-white/50 truncate">· {current.filename}</span>}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="lightbox-zoom-out" onClick={zoomOut} disabled={zoom <= MIN_ZOOM} className="p-2 rounded hover:bg-white/10 disabled:opacity-40" aria-label="Zoom out"><ZoomOut className="w-5 h-5" /></button>
          <span data-testid="lightbox-zoom-pct" className="text-xs tabular-nums text-white/70 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button data-testid="lightbox-zoom-in" onClick={zoomIn} disabled={zoom >= MAX_ZOOM} className="p-2 rounded hover:bg-white/10 disabled:opacity-40" aria-label="Zoom in"><ZoomIn className="w-5 h-5" /></button>
          <button data-testid="lightbox-reset" onClick={reset} className="p-2 rounded hover:bg-white/10" aria-label="Reset zoom"><Maximize2 className="w-5 h-5" /></button>
          <a data-testid="lightbox-download" href={current.url} download className="p-2 rounded hover:bg-white/10" aria-label="Download" onClick={(e) => e.stopPropagation()}><Download className="w-5 h-5" /></a>
          <button data-testid="lightbox-close" onClick={close} className="p-2 rounded hover:bg-white/10" aria-label="Close viewer"><X className="w-6 h-6" /></button>
        </div>
      </div>

      {/* Image stage */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
        {total > 1 && (
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/40 hover:bg-black/70 text-white z-10"
            aria-label="Previous photo"
          ><ChevronLeft className="w-6 h-6" /></button>
        )}
        <img
          data-testid="lightbox-image"
          src={current.url}
          alt={current.alt || current.filename || `Photo ${idx + 1}`}
          draggable={false}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onDoubleClick={onDoubleClick}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            cursor: zoom > 1 ? (dragRef.current.active ? 'grabbing' : 'grab') : 'zoom-in',
            transition: dragRef.current.active || pinchRef.current.active ? 'none' : 'transform 100ms ease-out',
            maxWidth: '100%',
            maxHeight: '100%',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
          }}
          className="object-contain"
        />
        {total > 1 && (
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/40 hover:bg-black/70 text-white z-10"
            aria-label="Next photo"
          ><ChevronRight className="w-6 h-6" /></button>
        )}
      </div>

      {/* Thumbnails strip (only when multiple) */}
      {total > 1 && (
        <div data-testid="lightbox-strip" className="px-3 py-2 overflow-x-auto whitespace-nowrap flex gap-2 border-t border-white/10 bg-black/40">
          {list.map((img, i) => (
            <button
              key={img.url + i}
              data-testid={`lightbox-thumb-${i}`}
              onClick={() => setIdx(i)}
              className={`shrink-0 w-14 h-14 rounded overflow-hidden border ${i === idx ? 'border-[#D4AF37]' : 'border-white/10 hover:border-white/40'}`}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" draggable={false} />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return createPortal(view, document.body);
};

export default Lightbox;
